import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Input } from "shared/components/inputs/Input";
import PageContainer from "shared/components/pagecontainer/PageContainer";
import { traduzirErro } from "shared/utils/traduzirErro";
import { maskCpfCnpj, maskTelefone } from "shared/utils/masks";
import { z } from "zod";
import { apiSignUp } from "../../shared/services/api";
import { cognitoGoogleSignIn, cognitoSignOut } from "../../shared/services/aws/cognito";
import styles from "./CdUsuario.module.scss";

const schema = z
  .object({
    name: z
      .string()
      .min(2, "O nome deve ter pelo menos 2 caracteres")
      .regex(/^[A-Za-zÀ-ÿ\s'-]+$/, "O nome não pode conter números"),
    email: z
      .string()
      .email("Insira um email válido")
      .transform((v) => v.toLowerCase()),
    cpf: z.string().min(11, "CPF inválido"),
    datanascimento: z.string().min(1, "Data de nascimento obrigatória"),
    telefone: z
      .string()
      .min(10, "Telefone inválido")
      .transform((v) => v.replace(/\D/g, "")),
    password: z
      .string()
      .min(
        8,
        "Mínimo 8 caracteres, incluindo uma letra maiúscula, uma letra minúscula e um caractere especial.",
      )
      .regex(/[A-Z]/, "1 letra maiúscula")
      .regex(/[a-z]/, "1 letra minúscula")
      .regex(/[^A-Za-z0-9]/, "1 caractere especial"),
    confirmPassword: z.string().min(1, "Confirme sua senha"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "As senhas não coincidem",
    path: ["confirmPassword"],
  });

type FormData = z.infer<typeof schema>;

export const CdUsuario = () => {
  const navigate = useNavigate();
  const [termosAceitos, setTermosAceitos] = useState(false);
  const [loading, setLoading] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    mode: "onChange",
  });

  useEffect(() => {
    cognitoSignOut().catch(() => {});
  }, []);

  const onSubmit = async (data: FormData) => {
    if (!termosAceitos) {
      alert("Você deve aceitar os termos.");
      return;
    }

    setLoading(true);

    try {
      await apiSignUp({
        name: data.name,
        email: data.email,
        datanascimento: data.datanascimento,
        phoneNumber: data.telefone.replace(/\D/g, ""),
        cpf: data.cpf.replace(/\D/g, ""),
        password: data.password,
      });

      navigate("/confirmar-email", {
        state: { email: data.email },
      });
    } catch (error: any) {
      alert(traduzirErro(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer title="Criar Conta">
      <div className={styles.page}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={styles.gridCadastro}>
            <div className={styles.containerInput}>
              <label className={`${styles.label} ${styles.required}`}>Nome Completo</label>
              <Input
                placeholder="Nome"
                type="text"
                name="name"
                register={register}
                error={errors.name?.message}
                customClass={styles.customInput}
              />
            </div>

            <div className={styles.containerInput}>
              <label className={`${styles.label} ${styles.required}`}>CPF</label>
              <Input
                placeholder="CPF"
                type="text"
                name="cpf"
                error={errors.cpf?.message}
                register={register("cpf", {
                  onChange: (e) => {
                    e.target.value = maskCpfCnpj(e.target.value);
                  },
                })}
                customClass={styles.customInput}
              />
            </div>

            <div className={styles.containerInput}>
              <label className={`${styles.label} ${styles.required}`}>Data de Nascimento</label>
              <Input
                placeholder="Data de Nascimento"
                type="date"
                name="datanascimento"
                register={register}
                error={errors.datanascimento?.message}
                customClass={styles.customInput}
              />
            </div>

            <div className={styles.containerInput}>
              <label className={`${styles.label} ${styles.required}`}>E-mail</label>
              <Input
                placeholder="E-mail"
                type="email"
                name="email"
                register={register}
                error={errors.email?.message}
                customClass={styles.customInput}
              />
            </div>

            <div className={styles.containerInput}>
              <label className={`${styles.label} ${styles.required}`}>Telefone / WhatsApp</label>
              <Input
                placeholder="Telefone"
                type="text"
                name="telefone"
                error={errors.telefone?.message}
                register={register("telefone", {
                  onChange: (e) => {
                    e.target.value = maskTelefone(e.target.value);
                  },
                })}
                customClass={styles.customInput}
              />
            </div>

            <div className={styles.containerInput}>
              <label className={`${styles.label} ${styles.required}`}>Senha</label>
              <Input
                placeholder="Senha"
                type="password-toggle"
                name="password"
                register={register}
                error={errors.password?.message}
                customClass={styles.customSenha}
              />
            </div>

            <div className={styles.containerInput}>
              <label className={`${styles.label} ${styles.required}`}>Confirmar Senha</label>
              <Input
                placeholder="Confirmar Senha"
                type="password-toggle"
                name="confirmPassword"
                register={register}
                error={errors.confirmPassword?.message}
                customClass={styles.customSenha}
              />
            </div>
          </div>

          <div className={styles.termosDiv}>
            <input
              type="checkbox"
              checked={termosAceitos}
              onChange={() => setTermosAceitos(!termosAceitos)}
            />
            <p className={styles.termos}>
              Ao criar uma conta você concorda com{" "}
              <a
                href="/TermosLojista.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.link2}
              >
                Contrato de Parceria e Termos de Lojista
              </a>
            </p>
          </div>

          <div className={styles.botaoContainer}>
            <button
              type="submit"
              disabled={!isValid || !termosAceitos || loading}
              className={`${styles.botao} ${
                isValid && termosAceitos ? styles.botaoAtivo : styles.botaoInativo
              }`}
            >
              {loading ? "Criando conta..." : "Criar Conta e verificar e-mail"}
            </button>
          </div>
        </form>

        <div className={styles.hrGoogle}>
          <span>ou continue com</span>
        </div>

        <div className={styles.containerGoogle}>
          <div className={styles.google}>
            <img src="/imagens/google-logo.png" alt="google" />
            <button className={styles.btnGoogle} onClick={cognitoGoogleSignIn}>
              Login do Google
            </button>
          </div>

          <p className={styles.linkConta}>
            Já tem conta?{" "}
            <button
              type="button"
              className={styles.link}
              onClick={() => navigate("/login")}
            >
              Entrar
            </button>
          </p>
        </div>
      </div>
    </PageContainer>
  );
};